import { ReactElement } from 'react';
import { Card, CardContent, Typography, Chip, Box } from '@mui/material';
import { useBrand } from '../../context/BrandContext';
import { useLanguage } from '../../context/LanguageContext';

type OfferStatus = 'available' | 'expired' | 'used';

interface Offer {
  offerNumber?: string;
  offerName?: string;
  offerDescription?: string;
  startDate?: string;
  endDate?: string;
  used?: boolean;
}

interface OfferCardProps {
  offer: Offer;
  onClick?: () => void;
  showDescription?: boolean;
}

const OFFER_IMAGES = ['/produit.jpeg', '/lavage.jpg', '/pneu.jpeg'];

const getImageByOfferNumber = (offerNum?: string): string => {
  const num = parseInt(String(offerNum || '0').replace(/\D/g, '') || '0', 10);
  return OFFER_IMAGES[num % 3];
};

const formatDate = (dateString?: string): string => 
  dateString?.replace(/(\d{2}:\d{2}):\d{2}/, '$1') || '';

const getOfferStatus = (offer: Offer): OfferStatus => {
  if (offer.used) return 'used';
  const now = new Date();
  if (offer.endDate && new Date(offer.endDate) < now) return 'expired';
  if (offer.startDate && new Date(offer.startDate) > now) return 'expired';
  return 'available';
};

const OfferCard = ({ offer, onClick, showDescription = false }: OfferCardProps): ReactElement => {
  const { brand } = useBrand();
  const { t } = useLanguage();
  const status = getOfferStatus(offer);
  const isClickable = status === 'available' && offer.offerNumber && onClick;

  const statusConfig = {
    available: { label: t('pages.offers.available'), color: 'success' as const },
    expired: { label: t('pages.offers.expired'), color: 'warning' as const },
    used: { label: t('pages.offers.used'), color: 'default' as const },
  }[status];

  return (
    <Card 
      sx={{ 
        minHeight: '140px',
        maxHeight: '160px',
        display: 'flex',
        cursor: isClickable ? 'pointer' : 'default',
        transition: 'all 0.2s',
        opacity: status === 'available' ? 1 : 0.7,
        background: `${brand.primaryColor}20`,
        borderRadius: '8px',
        border: '2px dashed #dee2e6',
        position: 'relative',
        overflow: 'visible',
        boxShadow: 'none',
        '&:hover': isClickable ? { transform: 'translateY(-4px)' } : {},
        '&::before': {
          content: '""',
          position: 'absolute',
          left: '-10px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: '#f3f3f9',
          zIndex: 2,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          right: '-10px',
          top: '50%',
          transform: 'translateY(-50%)',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: '#f3f3f9',
          zIndex: 2,
        },
      }}
      onClick={isClickable ? onClick : undefined}
    >
      <Box sx={{ 
        width: '120px',
        minWidth: '120px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `${brand.primaryColor}20`,
        borderRight: '2px dashed #dee2e6',
        p: 1.5,
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          right: '-10px',
          top: '-10px',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: '#f3f3f9',
          zIndex: 2,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          right: '-10px',
          bottom: '-10px',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          backgroundColor: '#f3f3f9',
          zIndex: 2,
        },
      }}>
        <Box
          component="img"
          src={getImageByOfferNumber(offer.offerNumber)}
          alt={offer.offerName || 'Offer'}
          sx={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
        />
      </Box>

      <CardContent sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column',
        p: 1.5,
        '&:last-child': { pb: 1.5 }
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 700, 
              flexGrow: 1,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontSize: '0.95rem',
              lineHeight: 1.3,
            }}
          >
            {offer.offerName || t('pages.offers.noTitle')}
          </Typography>
          <Chip 
            label={statusConfig.label} 
            size="small" 
            color={statusConfig.color}
            sx={{ fontWeight: 600, ml: 1 }}
          />
        </Box>
        
        {showDescription && offer.offerDescription && (
          <Typography variant="body2" sx={{ mb: 1.5, fontSize: '0.85rem', color: 'text.secondary' }}>
            {offer.offerDescription}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 'auto' }}>
          {offer.startDate && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              <strong>{t('pages.offers.startDate')} :</strong> {formatDate(offer.startDate)}
            </Typography>
          )}
          {offer.endDate && (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              <strong>{t('pages.offers.endDate')} :</strong> {formatDate(offer.endDate)}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default OfferCard;
